import { useEffect, useRef } from "react"
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  useColorModeValue,
  useDisclosure,
  PopoverProps,
} from "@chakra-ui/react"
import { InfoOutlineIcon } from "@chakra-ui/icons"

interface TooltipProps extends PopoverProps {
  children: React.ReactNode
  isFixed?: boolean
  contentProps?: React.ComponentProps<typeof PopoverContent>
}

export const Tooltip = ({
  children,
  contentProps,
  ...rest
}: TooltipProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
   const triggerRef = useRef<SVGSVGElement | null>(null)
  const tooltipBg = useColorModeValue('gray.900', 'primaryElevation')
  const iconColor = useColorModeValue('blackAlpha.500', 'whiteAlpha.500')

  useEffect(() => {
    if (!isOpen) return

    const handleScroll = () => onClose()
    const scrollParents: HTMLElement[] = []

    window.addEventListener("scroll", handleScroll, { passive: true })

    let parent = triggerRef.current?.parentElement

    while (parent) {
      const style = window.getComputedStyle(parent)
      const overflowY = style.overflowY

      const isScrollable =
        overflowY === "auto" ||
        overflowY === "scroll" ||
        overflowY === "overlay"

      if (isScrollable) {
        parent.addEventListener("scroll", handleScroll, { passive: true })
        scrollParents.push(parent)
      }

      parent = parent.parentElement
    }

    return () => {
      window.removeEventListener("scroll", handleScroll)
      scrollParents.forEach((el) =>
        el.removeEventListener("scroll", handleScroll)
      )
    }
  }, [isOpen, onClose])


  return (
    <Popover
      placement="bottom"
      trigger="click"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      {...rest}
    >
      <PopoverTrigger>
        <InfoOutlineIcon
          boxSize="15px"
          color={iconColor}
          cursor="pointer"
          mt={{ base: 1, md: 0 }}
          ref={triggerRef}
          onClick={(e) => {
            e.stopPropagation()
          }}
        />
      </PopoverTrigger>
      <PopoverContent
        w={{ base: '236px', md: '321px' }}
        maxW="90vw"
        bg={tooltipBg}
        color="white"
        zIndex={2000}  
        borderWidth={0}
        {...contentProps}
      >
        <PopoverArrow bg={tooltipBg} />
        <PopoverBody
          fontSize="14px"
          whiteSpace="normal"
          overflowWrap="anywhere"
          wordBreak="break-word"
          textAlign="start"
        >
          {children}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
